import React from 'react';
import Typography from '@material-ui/core/Typography';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { Button } from '@michelin/acid-components';
import { usePermissions, useTranslation } from '@michelin/central-provider';
import { TireProfileOVM } from '@michelin/fcp-view-models';
import { getOptionLabel } from '@michelin/select-options-provider';
import { useQueryClient } from '@tanstack/react-query';
import { useGetTireProfileList, useGetVehicleTypeList, useSaveTireProfile } from 'hooks/useTireProfile';
import { useSnackbar } from 'notistack';
import { ServiceOfferIds, getBaseUrl } from 'prefs-and-service-offers';
import { useHistory, useParams } from 'react-router-dom';
import { AlertBanner, AreaHeader, OpenFullButton, Panel, StatusTone, ToggleRow, WhyItMatters } from './components';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    table: {
      width: '100%',
      borderCollapse: 'collapse',
    },
    th: {
      textAlign: 'left',
      fontSize: '0.68rem',
      fontWeight: 700,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: theme.palette.text.secondary,
      padding: '0.6rem 1.25rem',
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    td: {
      fontSize: '0.85rem',
      color: theme.palette.text.primary,
      padding: '0.85rem 1.25rem',
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    axleCell: {
      fontWeight: 700,
    },
    subLabel: {
      display: 'block',
      fontSize: '0.75rem',
      fontWeight: 400,
      color: theme.palette.text.secondary,
      marginTop: 4,
    },
    instructions: {
      fontSize: '0.85rem',
      color: theme.palette.text.secondary,
      fontStyle: 'italic',
    },
    actionGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      flexWrap: 'wrap',
    },
    profileMeta: {
      fontSize: '0.8rem',
      color: theme.palette.text.secondary,
    },
    axleOptions: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem',
      padding: '0.9rem 1rem',
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    axleOption: {
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
      borderRadius: 999,
      fontSize: '0.78rem',
      fontWeight: 700,
      padding: '0.35rem 0.7rem',
      cursor: 'pointer',
      color: theme.palette.text.primary,
    },
    axleOptionSelected: {
      backgroundColor: '#e8eefb',
      borderColor: '#c5d8fb',
      color: '#103e79',
    },
    noDataCell: {
      fontSize: '0.82rem',
      color: theme.palette.text.secondary,
      padding: '1.1rem 1.25rem',
    },
    priorityColumn: {
      width: 95,
      whiteSpace: 'nowrap',
    },
    notesTitle: {
      fontSize: '0.84rem',
      fontWeight: 700,
      color: theme.palette.text.primary,
      marginBottom: 4,
    },
  }),
);

type AxleOptionKey = 'steer' | 'drive' | 'trailer' | 'carry' | 'lift' | 'pusher' | 'tag' | 'all_axles';

interface AxleOption {
  key: AxleOptionKey;
  label: string;
}

interface TirePriorityItem {
  priority: number;
  tireSize: string;
  tireType: string;
  brand: string;
  design: string;
  note: string;
}

const AXLE_OPTIONS: AxleOption[] = [
  { key: 'steer', label: 'Steer' },
  { key: 'drive', label: 'Drive' },
  { key: 'trailer', label: 'Trailer' },
  { key: 'carry', label: 'Carry' },
  { key: 'lift', label: 'Lift' },
  { key: 'pusher', label: 'Pusher' },
  { key: 'tag', label: 'Tag' },
  { key: 'all_axles', label: 'All Axles' },
];

function toPriorityItems(profile: TireProfileOVM | null | undefined, axleKey: AxleOptionKey): TirePriorityItem[] {
  const axle = profile?.axles?.[axleKey];
  const primary = axle?.tire_replacement_priority || [];

  if (primary.length > 0) {
    return primary
      .map((item, index) => ({
        priority: item.priority || index + 1,
        tireSize: item.tire_size || '-',
        tireType: item.tire_type || '-',
        brand: item.brand || '-',
        design: item.tread_design || '-',
        note: item.note || '-',
      }))
      .sort((a, b) => a.priority - b.priority);
  }

  const passengerLt = axle?.passenger_lt_priority || [];
  return passengerLt
    .map((item, index) => ({
      priority: item.priority || index + 1,
      tireSize: item.tire_size || '-',
      tireType: '-',
      brand: item.brand || '-',
      design: item.tread_design || '-',
      note: item.note || '-',
    }))
    .sort((a, b) => a.priority - b.priority);
}

function formatPriority(item?: TirePriorityItem): string {
  if (!item) return '-';
  return `${item.tireType} | ${item.brand} | ${item.design}`;
}

function resolveProfileType(profile?: TireProfileOVM): string {
  if (!profile?.main?.profile_type) return 'Unknown profile type';
  const mapped = getOptionLabel('profile_types', profile.main.profile_type);
  return mapped || profile.main.profile_type;
}

function resolveVehicleType(profile?: TireProfileOVM): string {
  if (!profile?.main?.vehicle_type_vmrs_code) return 'N/A';
  return (
    getOptionLabel('vehicle_types_onsite', profile.main.vehicle_type_vmrs_code) || profile.main.vehicle_type_vmrs_code
  );
}

function normalizeSpecialInstructions(value?: string): string {
  if (!value) return '';
  const trimmed = value.trim();
  const withoutOuterParagraph = trimmed.replace(/^<p>([\s\S]*)<\/p>$/i, '$1');
  return withoutOuterParagraph
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>\s*<p>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .trim();
}

function getProfileRecencyTimestamp(profile: TireProfileOVM): number {
  const dateValue = profile.update_date || profile.created_date;
  if (!dateValue) return 0;
  const parsed = Date.parse(dateValue);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function getLocationNumber(value?: string): string {
  return value ? value.split('~').pop() || '' : '';
}

function isProfileAssignedToAccount(profile: TireProfileOVM, accountNumber: string): boolean {
  if (profile.main?.assign_to_all_locations) return true;

  const assignedLocations = profile.main?.assigned_locations;
  if (Array.isArray(assignedLocations)) {
    return assignedLocations.some((location) => getLocationNumber(location) === accountNumber);
  }

  const locations = (profile as TireProfileOVM & { locations?: Array<Record<string, string>> }).locations || [];
  return locations.some((location) =>
    [location.hash_key, location.range_key, location.gsi1_hash_key, location.gsi1_range_key].some(
      (value) => getLocationNumber(value) === accountNumber,
    ),
  );
}

function hasAnyAxlePriority(profile: TireProfileOVM | null): boolean {
  if (!profile?.axles) return false;
  return Object.values(profile.axles).some((axle) => {
    if (!axle) return false;
    const hasPrimary = (axle.tire_replacement_priority || []).length > 0;
    const hasPassengerLt = (axle.passenger_lt_priority || []).length > 0;
    return hasPrimary || hasPassengerLt;
  });
}

interface TireAreaStatusResult {
  headerStatus: { label: string; tone: StatusTone };
  baseMissingReasons: string[];
  tireProfiles: TireProfileOVM[];
  selectedProfile: TireProfileOVM | null;
  missingVehicleTypes: string[];
  isLoadingProfiles: boolean;
  refreshStatus: () => Promise<any>;
}

export function useTireAreaStatus(): TireAreaStatusResult {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const { selectedAccount } = useParams<{ selectedAccount: string }>();
  const fleetId = permissions.location?.customer_number || selectedAccount || '';

  const {
    data: tireProfilesData = [],
    isLoading: isLoadingProfiles,
    refetch: refetchTireProfiles,
  } = useGetTireProfileList({ fleetId });
  const { data: vehicleTypesData = [], refetch: refetchVehicleTypes } = useGetVehicleTypeList({ fleetId });

  const tireProfiles = React.useMemo(
    () =>
      (Array.isArray(tireProfilesData) ? tireProfilesData : []).filter((profile) =>
        isProfileAssignedToAccount(profile, fleetId),
      ),
    [fleetId, tireProfilesData],
  );
  const vehicleTypes = React.useMemo(
    () => (Array.isArray(vehicleTypesData) ? vehicleTypesData : []),
    [vehicleTypesData],
  );

  const selectedProfile = React.useMemo(() => {
    if (!tireProfiles.length) return null;
    return tireProfiles.reduce<TireProfileOVM | null>((latest, profile) => {
      if (!latest) return profile;
      return getProfileRecencyTimestamp(profile) > getProfileRecencyTimestamp(latest) ? profile : latest;
    }, null);
  }, [tireProfiles]);

  const coveredVehicleTypes = React.useMemo(() => {
    const mapped = tireProfiles
      .map((profile) => profile.main?.vehicle_type_vmrs_code)
      .filter((vehicleType): vehicleType is string => !!vehicleType);
    return new Set(mapped);
  }, [tireProfiles]);

  const missingVehicleTypes = React.useMemo(
    () => vehicleTypes.filter((vehicleType) => !coveredVehicleTypes.has(vehicleType)),
    [vehicleTypes, coveredVehicleTypes],
  );

  const missingVehicleTypeLabels = React.useMemo(
    () =>
      missingVehicleTypes
        .map((vehicleType) => getOptionLabel('vehicle_types_onsite', vehicleType) || vehicleType)
        .join(', '),
    [missingVehicleTypes],
  );

  const baseMissingReasons = React.useMemo(() => {
    const reasons: string[] = [];
    if (!selectedProfile) reasons.push(t('No tire profile exists for this fleet yet.'));
    if (missingVehicleTypes.length > 0) {
      reasons.push(
        t('{{count}} vehicle type(s) do not have a correlated tire profile.', {
          count: missingVehicleTypes.length,
        }),
      );
      if (missingVehicleTypeLabels) {
        reasons.push(
          t('Missing vehicle types: {{vehicleTypes}}.', {
            vehicleTypes: missingVehicleTypeLabels,
          }),
        );
      }
    }
    if (selectedProfile && !hasAnyAxlePriority(selectedProfile)) {
      reasons.push(t('No axle tire size priorities are configured for this profile.'));
    }
    return reasons;
  }, [missingVehicleTypeLabels, missingVehicleTypes.length, selectedProfile, t]);

  const headerStatus: { label: string; tone: StatusTone } =
    baseMissingReasons.length > 0
      ? { label: t('{{count}} item(s) missing', { count: baseMissingReasons.length }), tone: 'warning' }
      : { label: t('Complete'), tone: 'success' };

  const refreshStatus = React.useCallback(() => {
    return Promise.all([refetchTireProfiles(), refetchVehicleTypes()]);
  }, [refetchTireProfiles, refetchVehicleTypes]);

  return {
    headerStatus,
    baseMissingReasons,
    tireProfiles,
    selectedProfile,
    missingVehicleTypes,
    isLoadingProfiles,
    refreshStatus,
  };
}

export function TireSubArea() {
  const { t } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const permissions = usePermissions();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const canCreateTire = permissions.allowsAction('tires.create');
  const { selectedAccount } = useParams<{ selectedAccount: string }>();
  const fleetId = permissions.location?.customer_number || selectedAccount || '';
  const urbanOnlyFleet = !!(
    permissions.location &&
    permissions.location.is_urban &&
    !permissions.location.is_commercial &&
    !permissions.location.override_is_commercial
  );

  const { isLoadingProfiles, selectedProfile } = useTireAreaStatus();
  const mostRecentProfile = selectedProfile;
  const saveTireProfile = useSaveTireProfile(fleetId, mostRecentProfile?.id);

  const handleDriverRequestToggle = async (checked: boolean) => {
    if (!mostRecentProfile || !fleetId) return;

    const updatedProfile: TireProfileOVM = {
      ...mostRecentProfile,
      main: {
        ...mostRecentProfile.main,
        driver_can_request_service: checked,
      },
    };

    await saveTireProfile.mutateAsync({ accountNumber: fleetId, data: updatedProfile, action: 'edit' });
    await queryClient.invalidateQueries(['get-tire-profile-list', fleetId]);
    enqueueSnackbar(t('Tire profile information saved.'), {
      variant: 'success',
      anchorOrigin: { vertical: 'top', horizontal: 'center' },
    });
  };

  const definedAxleOptions = React.useMemo(
    () => AXLE_OPTIONS.filter((axleOption) => !!mostRecentProfile?.axles?.[axleOption.key]),
    [mostRecentProfile],
  );

  const replacementRows = React.useMemo(
    () =>
      definedAxleOptions.flatMap((axleOption) => {
        const priorities = toPriorityItems(mostRecentProfile, axleOption.key);
        const firstPriority = priorities.find((item) => item.priority === 1) || priorities[0];
        const secondPriority = priorities.find((item) => item.priority === 2) || priorities[1];

        if (!firstPriority && !secondPriority) {
          return [
            {
              rowKey: `${axleOption.key}-empty`,
              axleLabel: axleOption.label,
              size: '-',
              firstPriority: '-',
              secondPriority: '-',
            },
          ];
        }

        const firstSize = firstPriority?.tireSize;
        const secondSize = secondPriority?.tireSize;
        const splitRows =
          !!firstPriority && !!secondPriority && !!firstSize && !!secondSize && firstSize !== secondSize;

        if (splitRows) {
          return [
            {
              rowKey: `${axleOption.key}-1`,
              axleLabel: axleOption.label,
              size: firstSize,
              firstPriority: formatPriority(firstPriority),
              secondPriority: '-',
            },
            {
              rowKey: `${axleOption.key}-2`,
              axleLabel: axleOption.label,
              size: secondSize,
              firstPriority: '-',
              secondPriority: formatPriority(secondPriority),
            },
          ];
        }

        return [
          {
            rowKey: `${axleOption.key}-single`,
            axleLabel: axleOption.label,
            size: firstSize || secondSize || '-',
            firstPriority: formatPriority(firstPriority),
            secondPriority: formatPriority(secondPriority),
          },
        ];
      }),
    [definedAxleOptions, mostRecentProfile],
  );

  const missingReasons = React.useMemo(() => {
    if (mostRecentProfile) return [];
    return [t('No tire profile exists for this fleet yet.')];
  }, [mostRecentProfile, t]);

  const status =
    missingReasons.length > 0
      ? { label: t('{{count}} item(s) missing', { count: missingReasons.length }), tone: 'warning' as const }
      : { label: t('Complete'), tone: 'success' as const };

  const legacyServiceOffer = urbanOnlyFleet ? ServiceOfferIds.bulkOrder : ServiceOfferIds.onCall;
  const openLegacyTire = () => history.push(`${getBaseUrl(false)}/${legacyServiceOffer}/tire-profile`);
  const openAddProfile = () => history.push(`${getBaseUrl(false)}/tire-profile/create`);

  const profileSpecialInstructions = normalizeSpecialInstructions(mostRecentProfile?.main?.special_instructions);

  return (
    <>
      <AreaHeader
        icon={<span aria-hidden>&#9711;</span>}
        iconColor="#e8eefb"
        title={t('Tire Preferences')}
        subtitle={t(
          'Which tire - by size, brand, and tread - belongs on each axle of each vehicle type, in priority order.',
        )}
        status={status}
      />

      {missingReasons.length > 0 && (
        <AlertBanner
          tone="warning"
          message={
            <span>
              <strong>{t('Correlated preferences missing:')}</strong> {missingReasons.join(' ')}
            </span>
          }
          action={
            <div className={classes.actionGroup}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                style={{ textTransform: 'none' }}
                disabled={!canCreateTire}
                onClick={canCreateTire ? openAddProfile : undefined}
              >
                {t('Add Profile')}
              </Button>
            </div>
          }
        />
      )}

      <Panel
        title={
          mostRecentProfile
            ? t('Replacement priority - {{profileName}}', {
                profileName: mostRecentProfile.main?.profile_name || t('Unnamed profile'),
              })
            : t('Replacement priority')
        }
        action={
          <div className={classes.actionGroup}>
            <Button variant="contained" size="small" style={{ textTransform: 'none' }} onClick={openLegacyTire}>
              {t('Legacy Tire')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              style={{ textTransform: 'none' }}
              disabled={!canCreateTire}
              onClick={canCreateTire ? openAddProfile : undefined}
            >
              {t('Add Profile')}
            </Button>
          </div>
        }
        flush
      >
        <table className={classes.table}>
          <thead>
            <tr>
              <th className={classes.th}>{t('AXLE')}</th>
              <th className={classes.th}>{t('SIZE')}</th>
              <th className={classes.th}>{t('1ST PRIORITY')}</th>
              <th className={classes.th}>{t('2ND PRIORITY')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoadingProfiles ? (
              <tr>
                <td className={classes.noDataCell} colSpan={4}>
                  {t('Loading tire profile data...')}
                </td>
              </tr>
            ) : mostRecentProfile && replacementRows.length > 0 ? (
              replacementRows.map((row) => (
                <tr key={row.rowKey}>
                  <td className={`${classes.td} ${classes.axleCell}`}>{t(row.axleLabel)}</td>
                  <td className={classes.td}>{row.size}</td>
                  <td className={classes.td}>{row.firstPriority}</td>
                  <td className={classes.td}>{row.secondPriority}</td>
                </tr>
              ))
            ) : mostRecentProfile ? (
              <tr>
                <td className={classes.noDataCell} colSpan={4}>
                  {t('No axle definitions found in the most recent tire profile.')}
                </td>
              </tr>
            ) : (
              <tr>
                <td className={classes.noDataCell} colSpan={4}>
                  {t('No tire profiles found. Use Add Profile to configure tire preferences.')}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {mostRecentProfile && (
          <div style={{ padding: '0.9rem 1.25rem', borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
            <Typography className={classes.profileMeta}>
              {t('Profile Type')}: {t(resolveProfileType(mostRecentProfile))}
              {' | '}
              {t('Vehicle Type')}: {t(resolveVehicleType(mostRecentProfile))}
            </Typography>
          </div>
        )}
      </Panel>

      <Panel title={t('Service rules')}>
        <ToggleRow
          label={t('Drivers can request tire service')}
          description={t('Open a tire request from the road without calling the office.')}
          checked={mostRecentProfile?.main?.driver_can_request_service !== false}
          onChange={handleDriverRequestToggle}
        />
        <div style={{ paddingTop: '0.85rem' }}>
          <Typography className={classes.notesTitle}>{t('Special instructions')}</Typography>
          <Typography className={classes.instructions}>
            {profileSpecialInstructions ||
              t(
                'No profile-level special instructions found. Use Add Profile or Legacy Tire to add remediation notes.',
              )}
          </Typography>
        </div>
      </Panel>

      <WhyItMatters>
        {t(
          'Set this once and the right tire goes on the right truck every time - even on unplanned roadside calls. Axle types supported: Steer, Drive, Trailer, Carry, Lift, Pusher, Tag, and All Axles.',
        )}
      </WhyItMatters>

      <OpenFullButton label={t('Open full Tire Preferences')} onClick={openLegacyTire} />
    </>
  );
}

export default TireSubArea;
